import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, Plus, X, Image, Printer } from 'lucide-react';
import html2canvas from 'html2canvas';
import { toast } from '../utils/toast';
import { useNamespaceEmpresa } from '../hooks/useNamespaceEmpresa';

const EMPTY_ARR = Object.freeze([]);

const CLASSIFICACOES = {
  CONSULTORIA: { label: 'Consultoria', cor: 'rgba(37,99,235,0.85)',  corHex: '#2563eb', mostraValor: true  },
  TREINAMENTO: { label: 'Treinamento', cor: 'rgba(124,58,237,0.85)', corHex: '#7c3aed', mostraValor: true  },
  CONTRATO:    { label: 'Contrato',    cor: 'rgba(234,179,8,0.85)',  corHex: '#ca8a04', mostraValor: true  },
  REUNIAO:     { label: 'Reunião',     cor: 'rgba(16,185,129,0.85)', corHex: '#10b981', mostraValor: false },
  ESTAGIARIA:  { label: 'Estagiária',  cor: 'rgba(236,72,153,0.85)', corHex: '#ec4899', mostraValor: false },
  EXTERNO:     { label: 'Externo',     cor: 'rgba(239,68,68,0.85)',   corHex: '#ef4444', mostraValor: true  },
  PESSOAL:     { label: 'Pessoal',     cor: 'rgba(234,88,12,0.85)',   corHex: '#ea580c', mostraValor: false },
};

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
               'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const modalBase = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  padding: '1rem',
};

const FORM_VAZIO = {
  data: '', horaInicio: '', horaFinal: '', profissionais: [],
  cliente: '', classificacao: 'CONSULTORIA', observacoes: '',
  pausaAlmoco: false, duracaoAlmoco: 60, linkMeet: '',
  faturarReuniao: false,
};

export const Calendario = ({
  agendamentos = [], clientes, profissionais, setAgendamentos, permissoesUsuario, isAdmin,
  anotacoes: anotacoesLegado, setAnotacoes: setAnotacoesLegado,
  anotacoesPorEmpresa, setAnotacoesPorEmpresa,
}) => {
  // Anotações namespaced por empresa: Kzero Consultoria herda lazy o legado;
  // demais empresas começam vazias. Setter wrapper escreve em mapa[slug].
  const [anotacoes, setAnotacoes] = useNamespaceEmpresa({
    mapaPorEmpresa: anotacoesPorEmpresa, setMapaPorEmpresa: setAnotacoesPorEmpresa,
    legado: anotacoesLegado, setLegado: setAnotacoesLegado, vazio: EMPTY_ARR,
  });

  // Classificações que o usuário pode ver (null = todas)
  const classifPermitidas = permissoesUsuario
    ? Object.keys(CLASSIFICACOES).filter(k => permissoesUsuario[`agenda.${k}`]?.ver !== false)
    : null;
  const podeVerValores = !permissoesUsuario || permissoesUsuario['agenda.valores']?.ver !== false;
  const [mesAtual, setMesAtual]             = useState(new Date());
  // Ids (string) dos clientes marcados no filtro; vazio = todos.
  const [filtroClientes, setFiltroClientes] = useState([]);
  const [menuClientes, setMenuClientes]     = useState(false);
  const [filtroClassif, setFiltroClassif]   = useState('');
  const [tooltip, setTooltip]               = useState(null);
  const [modalAberto, setModalAberto]       = useState(false);
  const [form, setForm]                     = useState(FORM_VAZIO);
  const [editandoId, setEditandoId]         = useState(null);

  const diasCalendario = useMemo(() => {
    const ano = mesAtual.getFullYear();
    const mes = mesAtual.getMonth();
    const primeiroDia = new Date(ano, mes, 1).getDay();
    const totalDias   = new Date(ano, mes + 1, 0).getDate();
    const dias = [];
    for (let i = 0; i < primeiroDia; i++) dias.push(null);
    for (let d = 1; d <= totalDias; d++) dias.push(d);
    return dias;
  }, [mesAtual]);

  const getAgsDia = (dia) => {
    if (!dia) return [];
    const dataStr = `${mesAtual.getFullYear()}-${String(mesAtual.getMonth()+1).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
    return agendamentos.filter(a => {
      if (a.data !== dataStr) return false;
      if (filtroClientes.length && !filtroClientes.includes(String(a.cliente))) return false;
      if (filtroClassif && (a.classificacao || a.tipo || 'CONSULTORIA') !== filtroClassif) return false;
      // Filtro por classificações permitidas pelo perfil
      if (classifPermitidas) {
        const classif = a.classificacao || a.tipo || 'CONSULTORIA';
        if (!classifPermitidas.includes(classif)) return false;
      }
      return true;
    });
  };

  const getTotalDia = (dia) => {
    if (!podeVerValores) return 0;
    return getAgsDia(dia).reduce((s, a) => {
      const classif = a.classificacao || a.tipo || 'CONSULTORIA';
      if (!CLASSIFICACOES[classif]?.mostraValor) return s;
      return s + parseFloat(a.valor || 0);
    }, 0);
  };

  const hoje = new Date();
  const ehHoje = (dia) =>
    dia && hoje.getDate()===dia &&
    hoje.getMonth()===mesAtual.getMonth() &&
    hoje.getFullYear()===mesAtual.getFullYear();

  const abrirModal = () => { setForm(FORM_VAZIO); setEditandoId(null); setModalAberto(true); };

  const abrirEdicao = (ag) => {
    setForm({
      data: ag.data || '',
      horaInicio: ag.horaInicio || '',
      horaFinal: ag.horaFinal || '',
      profissionais: ag.profissionais || (ag.profissional ? [ag.profissional] : []),
      cliente: ag.cliente || '',
      classificacao: ag.classificacao || ag.tipo || 'CONSULTORIA',
      observacoes: ag.observacoes || '',
      pausaAlmoco: ag.pausaAlmoco || false,
      duracaoAlmoco: ag.duracaoAlmoco || 60,
      linkMeet: ag.linkMeet || '',
      faturarReuniao: ag.faturarReuniao || false,
      // Versão que a pessoa abriu. Vai junto no save: se outra pessoa gravou
      // enquanto o formulário estava aberto, o banco recusa em vez de apagar
      // o que ela escreveu (mig 022; incidente de 29/09/2026).
      _updated_at: ag._updated_at ?? null,
    });
    setEditandoId(ag.id);
    setModalAberto(true);
  };

  const fecharModal = () => { setModalAberto(false); setEditandoId(null); };

  const excluirEvento = () => {
    if (!editandoId) return;
    if (window.confirm('Excluir este evento?')) {
      const evento = agendamentos.find(a => a.id === editandoId);
      if (setAgendamentos) setAgendamentos(agendamentos.filter(a => a.id !== editandoId));
      // Se for evento de treinamento, remove também do módulo Treinamentos
      if (evento?.origemTreinamento && evento?.origemEvId) {
        try {
          const agTr = JSON.parse(localStorage.getItem('kzerodiary_agenda_trein') || '[]');
          localStorage.setItem('kzerodiary_agenda_trein', JSON.stringify(agTr.filter(e => e.id !== evento.origemEvId)));
        } catch {}
      }
      toast.sucesso('Evento excluído.');
      fecharModal();
    }
  };

  const calcularHoras = (inicio, final, pausaAlmoco, duracaoAlmoco) => {
    if (!inicio || !final) return 0;
    const [hi, mi] = inicio.split(':').map(Number);
    const [hf, mf] = final.split(':').map(Number);
    let minutos = (hf * 60 + mf) - (hi * 60 + mi);
    if (pausaAlmoco) minutos -= (duracaoAlmoco || 60);
    return minutos > 0 ? minutos / 60 : 0;
  };

  const calcularValor = () => {
    if (form.classificacao === 'PESSOAL') return 0;
    if (form.classificacao === 'REUNIAO' && !form.faturarReuniao) return 0;
    const horas = calcularHoras(form.horaInicio, form.horaFinal, form.pausaAlmoco, form.duracaoAlmoco);
    const cliente = clientes.find(c => String(c.id) === String(form.cliente));
    const valorHora = parseFloat(cliente?.valorHora || 0);
    return horas * valorHora;
  };

  const horasCalculadas = calcularHoras(form.horaInicio, form.horaFinal, form.pausaAlmoco, form.duracaoAlmoco);
  const valorCalculado  = calcularValor();

  const toggleProfissional = (p) => {
    setForm(f => ({
      ...f,
      profissionais: (f.profissionais||[]).includes(p)
        ? (f.profissionais||[]).filter(x => x !== p)
        : [...(f.profissionais||[]), p],
    }));
  };

  const salvarEvento = () => {
    if (!form.data) { toast.aviso('Preencha a data do evento.'); return; }
    if (!(form.profissionais||[]).length) { toast.aviso('Selecione pelo menos um profissional.'); return; }
    const reuniaoNaoFaturavel = form.classificacao === 'REUNIAO' && !form.faturarReuniao;
    const valorCalc = reuniaoNaoFaturavel
      ? '0.00'
      : (parseFloat(horasCalculadas.toFixed(1)) * parseFloat(clientes.find(c => String(c.id) === String(form.cliente))?.valorHora || 0)).toFixed(2);
    const dados = {
      ...form,
      profissional: (form.profissionais||[]).join(', '), // retrocompatibilidade
      tipo: form.classificacao,
      horas: horasCalculadas.toFixed(1),
      valor: valorCalc,
    };
    if (editandoId) {
      const eventoAntes = agendamentos.find(a => a.id === editandoId);
      if (setAgendamentos) setAgendamentos(agendamentos.map(a => a.id === editandoId ? { ...a, ...dados } : a));
      // Sincroniza edição com módulo Treinamentos se for evento de origem
      if (eventoAntes?.origemTreinamento && eventoAntes?.origemEvId) {
        try {
          const agTr = JSON.parse(localStorage.getItem('kzerodiary_agenda_trein') || '[]');
          const atualizado = agTr.map(e => e.id === eventoAntes.origemEvId
            ? { ...e, titulo: dados.observacoes || e.titulo, data: dados.data, hora: dados.horaInicio, horaFim: dados.horaFinal }
            : e
          );
          localStorage.setItem('kzerodiary_agenda_trein', JSON.stringify(atualizado));
        } catch {}
      }
      // Atualiza data/título na anotação vinculada quando a data do evento muda (preserva conteúdo)
      if (setAnotacoes && anotacoes && eventoAntes && eventoAntes.data !== dados.data) {
        // Busca 1: pelo agendamentoId (vinculação direta)
        const porId = anotacoes.find(a => String(a.agendamentoId) === String(editandoId));
        // Busca 2: pelo cliente + data antiga do evento (criadas manualmente ou antes da feature)
        const porDataAntiga = !porId && eventoAntes.cliente
          ? anotacoes.find(a =>
              String(a.clienteId) === String(eventoAntes.cliente) &&
              a.data === eventoAntes.data
            )
          : null;
        const anotVinculada = porId || porDataAntiga;
        if (anotVinculada) {
          const novoTitulo = `Atendimento — ${dados.data.split('-').reverse().join('/')}`;
          setAnotacoes(anotacoes.map(a =>
            a.id === anotVinculada.id ? { ...a, data: dados.data, titulo: novoTitulo } : a
          ));
        }
      }
      toast.sucesso('Evento atualizado!');
    } else {
      const novoId = Date.now();
      if (setAgendamentos) setAgendamentos([...agendamentos, { ...dados, id: novoId }]);
      // Auto-criar anotação para CONSULTORIA ou EXTERNO com cliente preenchido
      if (
        setAnotacoes && anotacoes &&
        dados.cliente &&
        (dados.classificacao === 'CONSULTORIA' || dados.classificacao === 'EXTERNO' || dados.classificacao === 'CONTRATO')
      ) {
        const jaExiste = anotacoes.some(
          a => String(a.clienteId) === String(dados.cliente) && a.data === dados.data && a.agendamentoId === novoId
        );
        if (!jaExiste) {
          const novaAnot = {
            id: novoId + 1,
            clienteId: dados.cliente,
            data: dados.data,
            titulo: `Atendimento — ${dados.data.split('-').reverse().join('/')}`,
            conteudo: '',
            profissional: (dados.profissionais||[]).join(', '),
            anexos: [],
            autoGerada: true,
            agendamentoId: novoId,
          };
          setAnotacoes([...anotacoes, novaAnot]);
        }
      }
      toast.sucesso('Evento cadastrado!');
    }
    fecharModal();
  };

  const isPessoal = form.classificacao === 'PESSOAL';

  const toggleFiltroCliente = (id) => {
    const k = String(id);
    setFiltroClientes(f => f.includes(k) ? f.filter(x => x !== k) : [...f, k]);
  };

  const nomesClientesFiltro = filtroClientes
    .map(id => clientes.find(c => String(c.id) === id)?.nome)
    .filter(Boolean);

  const rotuloFiltroClientes = !filtroClientes.length
    ? 'Todos os clientes'
    : filtroClientes.length === 1
      ? (nomesClientesFiltro[0] || '1 cliente')
      : `${filtroClientes.length} clientes`;

  // Imagem 1080x1080. Sem cliente marcado: o mês inteiro, com os atendimentos
  // nas cores das classificações. Um cliente marcado: os dias de atendimento
  // dele. Vários marcados: uma imagem só, no formato do mês inteiro, com os
  // atendimentos deles. Todas respeitam o filtro de classificação e o que o
  // perfil pode ver (antes a do cliente ignorava).
  const gerarImagemCalendario = async () => {
    if (!filtroClientes.length) { await gerarImagemGeral(); return; }
    if (filtroClientes.length === 1) { await gerarImagemCliente(filtroClientes[0]); return; }
    await gerarImagemGeral(filtroClientes);
  };

  const escHtml = (v) => String(v ?? '').replace(/[&<>"']/g, ch => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[ch]));
  const encurtar = (v, max) => { const t = String(v ?? '').trim(); return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t; };

  // Agendamentos visíveis de um dia, com o filtro de classificação e o perfil;
  // `clienteId` restringe a um cliente (imagem por cliente).
  const agsVisiveisDoDia = (dia, clienteId) => {
    const dataStr = `${mesAtual.getFullYear()}-${String(mesAtual.getMonth()+1).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
    return agendamentos.filter(a => {
      if (a.data !== dataStr) return false;
      if (clienteId != null && String(a.cliente) !== String(clienteId)) return false;
      const classif = a.classificacao || a.tipo || 'CONSULTORIA';
      if (filtroClassif && classif !== filtroClassif) return false;
      if (classifPermitidas && !classifPermitidas.includes(classif)) return false;
      return true;
    }).sort((a, b) => (a.horaInicio || '').localeCompare(b.horaInicio || ''));
  };

  // Moldura comum: faixa do topo, dias da semana, grade e rodapé.
  const montarImagem = ({ rotulo, titulo, celula, rodapeEsquerda }) => {
    const ano = mesAtual.getFullYear();
    const mes = mesAtual.getMonth();
    const semanas = [];
    let semana = [];
    diasCalendario.forEach(d => { semana.push(d); if (semana.length === 7) { semanas.push(semana); semana = []; } });
    if (semana.length) { while (semana.length < 7) semana.push(null); semanas.push(semana); }

    const tamTitulo = titulo.length <= 18 ? 52 : titulo.length <= 28 ? 42 : 34;
    const gap = 10;
    const alturaGrade = 1080 - 222 - 36 - 46 - 128;
    const alturaLinha = Math.floor((alturaGrade - gap * (semanas.length - 1)) / semanas.length);

    return `
      <div id="cal-img" style="width:1080px;height:1080px;background:#faf8ff;font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif;position:relative;overflow:hidden;">
        <div style="height:222px;background:linear-gradient(135deg,#2e1065 0%,#5b21b6 55%,#7c3aed 100%);padding:0 56px;display:flex;align-items:center;justify-content:space-between;position:relative;overflow:hidden;">
          <div style="position:absolute;right:-90px;top:-120px;width:360px;height:360px;border-radius:50%;background:rgba(255,255,255,0.07);"></div>
          <div style="position:absolute;right:170px;bottom:-150px;width:260px;height:260px;border-radius:50%;background:rgba(255,255,255,0.05);"></div>
          <div style="max-width:640px;position:relative;">
            <div style="font-size:17px;font-weight:700;color:rgba(255,255,255,0.72);letter-spacing:4px;text-transform:uppercase;margin-bottom:10px;">${escHtml(rotulo)}</div>
            <div style="font-size:${tamTitulo}px;line-height:1.1;font-weight:800;color:#fff;">${escHtml(titulo)}</div>
          </div>
          <div style="text-align:right;position:relative;">
            <div style="font-size:46px;line-height:1;font-weight:800;color:#fff;letter-spacing:3px;text-transform:uppercase;">${MESES[mes]}</div>
            <div style="font-size:24px;font-weight:600;color:rgba(255,255,255,0.7);margin-top:8px;letter-spacing:6px;">${ano}</div>
          </div>
        </div>

        <div style="padding:36px 48px 0;">
          <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:${gap}px;height:46px;">
            ${DIAS_SEMANA.map((d, i) => `<div style="text-align:center;font-size:17px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:${i === 0 || i === 6 ? '#b3a8cc' : '#6d5f8d'};">${d}</div>`).join('')}
          </div>
          <div style="display:grid;grid-template-columns:repeat(7,1fr);grid-auto-rows:${alturaLinha}px;gap:${gap}px;">
            ${semanas.map(sem => sem.map((dia, i) => dia ? celula(dia, i === 0 || i === 6) : '<div></div>').join('')).join('')}
          </div>
        </div>

        <div style="position:absolute;left:48px;right:48px;bottom:0;height:128px;border-top:2px solid #ebe5f7;display:flex;align-items:center;justify-content:space-between;gap:24px;">
          <div style="display:flex;align-items:center;gap:18px;flex-wrap:wrap;max-width:700px;">${rodapeEsquerda}</div>
          <img src="/icons/kzero-logo-full.png" style="height:62px;object-fit:contain;" />
        </div>
      </div>`;
  };

  const baixarImagem = async (html, nomeArquivo) => {
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;z-index:-1;';
    container.innerHTML = html;
    document.body.appendChild(container);
    try {
      // Espera o logo carregar: sem isso a primeira imagem saía sem ele.
      await Promise.all([...container.querySelectorAll('img')].map(img =>
        img.complete ? null : new Promise(r => { img.onload = r; img.onerror = r; })));
      const el = container.querySelector('#cal-img');
      const canvas = await html2canvas(el, { scale: 1, width: 1080, height: 1080, useCORS: true, backgroundColor: '#faf8ff' });
      const link = document.createElement('a');
      link.download = nomeArquivo;
      link.href = canvas.toDataURL('image/png');
      link.click();
      return true;
    } catch (e) {
      toast.erro('Não foi possível gerar a imagem. Tente novamente.');
      return false;
    } finally {
      document.body.removeChild(container);
    }
  };

  const nomeDeArquivo = (base) => {
    const limpo = `${base}_${MESES[mesAtual.getMonth()]}_${mesAtual.getFullYear()}`
      .normalize('NFD').replace(/\p{M}/gu, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '');
    return `${limpo}.png`;
  };

  const gerarImagemCliente = async (clienteId) => {
    const clienteObj = clientes.find(c => String(c.id) === String(clienteId));
    if (!clienteObj) return;

    const dias = diasCalendario.filter(d => d && agsVisiveisDoDia(d, clienteId).length > 0);
    if (dias.length === 0) {
      toast.aviso(`Nenhum agendamento de ${clienteObj.nome} no mês selecionado.`);
      return;
    }
    const ativos = new Set(dias);

    const celula = (dia, fimDeSemana) => ativos.has(dia)
      ? `<div style="border-radius:18px;background:linear-gradient(150deg,#5b21b6,#7c3aed);display:flex;align-items:center;justify-content:center;font-size:36px;font-weight:800;color:#fff;">${dia}</div>`
      : `<div style="border-radius:18px;background:${fimDeSemana ? '#f3f0f9' : '#ffffff'};border:1px solid #ece6f7;display:flex;align-items:center;justify-content:center;font-size:27px;font-weight:500;color:${fimDeSemana ? '#b3a8cc' : '#4b4363'};">${dia}</div>`;

    const total = dias.length;
    const rodape = `
      <div style="display:flex;align-items:center;gap:14px;">
        <div style="width:30px;height:30px;border-radius:9px;background:linear-gradient(150deg,#5b21b6,#7c3aed);"></div>
        <div>
          <div style="font-size:26px;font-weight:800;color:#2e1065;">Dias de atendimento</div>
          <div style="font-size:18px;font-weight:500;color:#6d5f8d;margin-top:2px;">${total} ${total === 1 ? 'dia' : 'dias'} em ${MESES[mesAtual.getMonth()].toLowerCase()}</div>
        </div>
      </div>`;

    const html = montarImagem({ rotulo: 'Calendário de atendimentos', titulo: clienteObj.nome, celula, rodapeEsquerda: rodape });
    if (await baixarImagem(html, nomeDeArquivo(clienteObj.nome))) {
      toast.sucesso(`Imagem do calendário de ${clienteObj.nome} gerada.`);
    }
  };

  // Mês inteiro: até 3 atendimentos por dia, nas cores das classificações.
  // `clienteIds` restringe aos clientes marcados e põe os nomes no título.
  const gerarImagemGeral = async (clienteIds) => {
    const nomes = clienteIds
      ? [...nomesClientesFiltro].sort((a, b) => a.localeCompare(b, 'pt-BR'))
      : [];
    // Com mais de 3 nomes o título não cabe na faixa do topo.
    const nomeClientes = nomes.length > 3 ? `${nomes.length} clientes` : nomes.join(', ');
    const nomeClassif = filtroClassif ? CLASSIFICACOES[filtroClassif]?.label || 'Atendimentos' : '';
    const usadas = new Set();
    let total = 0;
    const celula = (dia, fimDeSemana) => {
      const ags = agsVisiveisDoDia(dia).filter(a => !clienteIds || clienteIds.includes(String(a.cliente)));
      total += ags.length;
      ags.forEach(ag => usadas.add(ag.classificacao || ag.tipo || 'CONSULTORIA'));
      // Cabem 3 linhas na célula: com mais de 3, mostra 2 e o '+N outros'.
      const visiveis = ags.length > 3 ? 2 : 3;
      const pills = ags.slice(0, visiveis).map(ag => {
        const classif = ag.classificacao || ag.tipo || 'CONSULTORIA';
        usadas.add(classif);
        const cliente = clientes.find(c => String(c.id) === String(ag.cliente));
        const nome = ag.origemTreinamento ? 'Treinamento' : (cliente?.nome || CLASSIFICACOES[classif]?.label || '');
        return `<div style="background:${CLASSIFICACOES[classif]?.corHex || '#7c3aed'};color:#fff;border-radius:6px;padding:3px 7px;font-size:13px;font-weight:600;line-height:1.25;white-space:nowrap;overflow:hidden;margin-top:4px;">${escHtml(encurtar(nome, 15))}</div>`;
      }).join('');
      const resto = ags.length - visiveis;
      const mais = resto > 0 ? `<div style="font-size:13px;font-weight:700;color:#6d5f8d;margin-top:5px;">+${resto} ${resto === 1 ? 'outro' : 'outros'}</div>` : '';
      return `<div style="border-radius:14px;background:${fimDeSemana ? '#f3f0f9' : '#ffffff'};border:1px solid #ece6f7;padding:8px 8px 6px;overflow:hidden;">
        <div style="font-size:19px;font-weight:800;color:${ags.length ? '#2e1065' : (fimDeSemana ? '#b3a8cc' : '#8a7fa6')};">${dia}</div>${pills}${mais}</div>`;
    };

    // A grade é montada antes do rodapé: é ela que preenche `usadas` e `total`.
    const semRodape = montarImagem({
      rotulo: nomeClientes && nomeClassif ? nomeClassif : 'Agenda',
      titulo: nomeClientes || nomeClassif || 'Atendimentos do mês',
      celula,
      rodapeEsquerda: '%%RODAPE%%',
    });
    if (total === 0) {
      toast.aviso('Nenhum agendamento no mês selecionado com os filtros aplicados.');
      return;
    }
    const legenda = Object.entries(CLASSIFICACOES)
      .filter(([k]) => usadas.has(k))
      .map(([, v]) => `<div style="display:flex;align-items:center;gap:8px;font-size:18px;font-weight:600;color:#4b4363;"><span style="width:18px;height:18px;border-radius:5px;background:${v.corHex};display:inline-block;"></span>${escHtml(v.label)}</div>`)
      .join('');
    const html = semRodape.replace('%%RODAPE%%', legenda);
    const base = ['Agenda', nomeClientes, nomeClassif].filter(Boolean).join('_');
    if (await baixarImagem(html, nomeDeArquivo(base))) toast.sucesso('Imagem do calendário gerada.');
  };

  // Impressão do mês com os filtros da tela, nas cores das classificações.
  // Janela própria em paisagem: imprimir a tela inteira levaria menu e topo junto.
  const imprimirCalendario = () => {
    const esc = (v) => String(v ?? '').replace(/[&<>"']/g, ch => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[ch]));
    const ano = mesAtual.getFullYear();
    const mes = mesAtual.getMonth();

    const semanas = [];
    let semana = [];
    diasCalendario.forEach(d => {
      semana.push(d);
      if (semana.length === 7) { semanas.push(semana); semana = []; }
    });
    if (semana.length) { while (semana.length < 7) semana.push(null); semanas.push(semana); }

    const usadas = new Set();
    const celula = (dia) => {
      if (!dia) return '<td class="vazia"></td>';
      const ags = [...getAgsDia(dia)].sort((a, b) => (a.horaInicio || '').localeCompare(b.horaInicio || ''));
      const evs = ags.map(ag => {
        const classif = ag.classificacao || ag.tipo || 'CONSULTORIA';
        usadas.add(classif);
        const cliente = clientes.find(c => String(c.id) === String(ag.cliente));
        const profs = (ag.profissionais || []).length ? ag.profissionais.join(', ') : (ag.profissional || '');
        const hora = `${ag.horaInicio || '--:--'}${ag.horaFinal ? ` – ${ag.horaFinal}` : ''}`;
        return `<div class="ev" style="background:${CLASSIFICACOES[classif]?.corHex || '#7c3aed'}">
          <b>${esc(hora)}</b>
          <span>${esc(ag.origemTreinamento ? 'Treinamento' : (cliente?.nome || CLASSIFICACOES[classif]?.label || ''))}</span>
          ${profs && !ag.origemTreinamento ? `<small>${esc(profs)}</small>` : ''}
        </div>`;
      }).join('');
      return `<td><div class="num">${dia}</div>${evs}</td>`;
    };

    const corpo = semanas.map(sem => `<tr>${sem.map(celula).join('')}</tr>`).join('');
    const legenda = Object.entries(CLASSIFICACOES)
      .filter(([k]) => usadas.has(k))
      .map(([, v]) => `<span class="leg"><i style="background:${v.corHex}"></i>${esc(v.label)}</span>`)
      .join('');
    const subtitulo = [
      nomesClientesFiltro.length ? nomesClientesFiltro.join(' · ') : '',
      filtroClassif ? CLASSIFICACOES[filtroClassif]?.label : '',
    ].filter(Boolean).join(' — ');
    const titulo = `${MESES[mes]} de ${ano}`;

    const janela = window.open('', '_blank');
    if (!janela) { toast.aviso('Permita pop-ups para imprimir o calendário.'); return; }
    janela.document.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
      <title>Agenda — ${esc(titulo)}${subtitulo ? ` — ${esc(subtitulo)}` : ''}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { margin: 0; padding: 16px; font-family: 'Segoe UI', Arial, sans-serif; color: #1c1530; }
        header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #7c3aed; padding-bottom: 8px; margin-bottom: 10px; }
        h1 { margin: 0; font-size: 24px; color: #3b0764; }
        .sub { font-size: 13px; color: #5f5874; margin-top: 2px; }
        header img { height: 44px; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        th { background: #7c3aed; color: #fff; font-size: 11px; letter-spacing: .08em; padding: 6px 0; text-transform: uppercase; }
        td { border: 1px solid #ddd6ee; vertical-align: top; height: 92px; padding: 3px 4px; }
        td.vazia { background: #f7f5fb; }
        .num { font-size: 12px; font-weight: 700; color: #5f5874; margin-bottom: 2px; }
        .ev { color: #fff; border-radius: 4px; padding: 2px 5px; margin-bottom: 2px; font-size: 10px; line-height: 1.25; display: flex; flex-direction: column; break-inside: avoid; }
        .ev span { font-weight: 600; }
        .ev small { font-size: 9px; opacity: .9; }
        footer { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 8px; font-size: 11px; color: #5f5874; }
        .leg { display: inline-flex; align-items: center; gap: 5px; }
        .leg i { width: 11px; height: 11px; border-radius: 3px; display: inline-block; }
        tr { break-inside: avoid; }
      </style></head><body>
      <header>
        <div><h1>${esc(titulo)}</h1>${subtitulo ? `<div class="sub">${esc(subtitulo)}</div>` : ''}</div>
        <img src="${window.location.origin}/icons/kzero-logo-full.png" alt="Kzero" />
      </header>
      <table>
        <thead><tr>${DIAS_SEMANA.map(d => `<th>${d}</th>`).join('')}</tr></thead>
        <tbody>${corpo}</tbody>
      </table>
      <footer>${legenda}</footer>
      <script>window.onload=function(){setTimeout(function(){window.print();},300);}<\/script>
      </body></html>`);
    janela.document.close();
  };

  return (
    <div className="cal-container" style={s.container}>
      {/* Cabeçalho */}
      <div className="cal-header" style={s.header}>
        <div style={s.headerLeft}>
          <h2 className="cal-titulo" style={s.titulo}>{MESES[mesAtual.getMonth()]} de {mesAtual.getFullYear()}</h2>
          <div style={s.navBtns}>
            <button style={s.navBtn} onClick={() => setMesAtual(new Date(mesAtual.getFullYear(), mesAtual.getMonth()-1))}>
              <ChevronLeft size={18}/>
            </button>
            <button style={s.navBtn} onClick={() => setMesAtual(new Date(mesAtual.getFullYear(), mesAtual.getMonth()+1))}>
              <ChevronRight size={18}/>
            </button>
          </div>
        </div>
        <div className="cal-header-right" style={s.headerRight}>
          {/* Filtro cliente — permite marcar vários */}
          <div style={{ position:'relative' }}>
            <button
              type="button"
              className="cal-select"
              style={{ ...s.select, minWidth:220, display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}
              onClick={() => setMenuClientes(v => !v)}
              aria-expanded={menuClientes}
            >
              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{rotuloFiltroClientes}</span>
              <ChevronDown size={14}/>
            </button>
            {menuClientes && (
              <>
                <div style={{ position:'fixed', inset:0, zIndex:40 }} onClick={() => setMenuClientes(false)}/>
                <div style={s.menuClientes}>
                  <button type="button" style={s.menuClientesTodos} onClick={() => setFiltroClientes([])}>
                    Todos os clientes
                  </button>
                  {[...clientes]
                    .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))
                    .map(c => (
                      <label key={c.id} style={s.menuClientesItem}>
                        <input
                          type="checkbox"
                          checked={filtroClientes.includes(String(c.id))}
                          onChange={() => toggleFiltroCliente(c.id)}
                        />
                        {c.nome}
                      </label>
                    ))}
                </div>
              </>
            )}
          </div>
          {/* Filtro classificação */}
          <select className="cal-select" style={s.select} value={filtroClassif} onChange={e => setFiltroClassif(e.target.value)}>
            <option value="">Todas as classificações</option>
            {Object.entries(CLASSIFICACOES)
              .filter(([k]) => !classifPermitidas || classifPermitidas.includes(k))
              .map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          {(filtroClientes.length > 0 || filtroClassif) && (
            <button style={s.btnLimpar} onClick={() => { setFiltroClientes([]); setFiltroClassif(''); }}>
              <X size={14}/> Limpar
            </button>
          )}
          <button
            style={{ ...s.btnNovo, background:'#0f766e', gap:6 }}
            onClick={imprimirCalendario}
            title="Imprimir o mês em cores, com os filtros aplicados"
          >
            <Printer size={15}/> Imprimir
          </button>
          <button
            style={{ ...s.btnNovo, background:'#5b21b6', gap:6 }}
            onClick={gerarImagemCalendario}
            title="Gerar imagem 1080x1080: sem cliente marcado, o mês inteiro; com clientes marcados, uma imagem com os dias de atendimento de cada um"
          >
            <Image size={15}/> Gerar Imagem
          </button>
          <a
            href="https://teams.microsoft.com/l/meeting/new"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...s.btnNovo, background:'#6264a7', textDecoration:'none' }}
          >
            🎥 Iniciar Reunião
          </a>
          <button className="cal-btn" style={s.btnNovo} onClick={abrirModal}>
            <Plus size={16}/> Novo Evento
          </button>
        </div>
      </div>

      {/* Grade */}
      <div className="cal-grade" style={s.grade}>
        {DIAS_SEMANA.map(d => <div key={d} className="cal-cab-dia" style={s.cabDia}>{d}</div>)}
        {diasCalendario.map((dia, idx) => {
          const ags   = getAgsDia(dia);
          const total = getTotalDia(dia);
          return (
            <div key={idx} className="cal-celula" style={{ ...s.celula, ...(dia ? {} : s.celulaVazia), ...(ehHoje(dia) ? s.celulaHoje : {}) }}>
              {dia && (
                <>
                  <span style={{ ...s.numDia, ...(ehHoje(dia) ? s.numDiaHoje : {}) }}>{dia}</span>
                  <div style={s.eventosLista}>
                    {ags.map(ag => {
                      const classif = ag.classificacao || ag.tipo || 'CONSULTORIA';
                      const cor     = CLASSIFICACOES[classif]?.cor || 'rgba(124,58,237,0.85)';
                      const cliente = clientes.find(c => String(c.id) === String(ag.cliente));
                      return (
                        <div
                          key={ag.id}
                          className="cal-evento"
                          style={{ ...s.evento, background: CLASSIFICACOES[classif]?.corHex || cor }}
                          onMouseEnter={e => setTooltip({ ag, cliente, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setTooltip(null)}
                          onClick={() => { setTooltip(null); abrirEdicao(ag); }}
                        >
                          <span style={s.eventoHora}>{ag.horaInicio || '--:--'}</span>
                          {ag.origemTreinamento
                            ? <span style={s.eventoCliente}>Treinamento</span>
                            : <>
                                {(ag.profissionais||[]).length > 0
                                  ? <span style={s.eventoProf}>{(ag.profissionais||[]).map(p=>p.split(' ')[0]).join(', ')}</span>
                                  : ag.profissional && <span style={s.eventoProf}>{ag.profissional.split(' ')[0]}</span>
                                }
                                {cliente && <span style={s.eventoCliente}>{cliente.nome.substring(0,12)}</span>}
                              </>
                          }
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Legenda */}
      <div className="cal-legenda" style={s.legenda}>
        {Object.entries(CLASSIFICACOES).map(([k,v]) => (
          <div key={k} style={s.legendaItem}>
            <div style={{ ...s.legendaCor, background: v.corHex }}/>
            <span style={s.legendaNome}>{v.label}</span>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {tooltip && (() => {
        const classif = tooltip.ag.classificacao || tooltip.ag.tipo || 'CONSULTORIA';
        const mostraValor = CLASSIFICACOES[classif]?.mostraValor && podeVerValores;
        return (
          <div style={{ ...s.tooltip, top: tooltip.y+12, left: tooltip.x+12 }}>
            {tooltip.ag.origemTreinamento
              ? <strong>Treinamento</strong>
              : <strong>{tooltip.cliente?.nome || 'Sem cliente'}</strong>
            }
            <span>🕐 {tooltip.ag.horaInicio || '--:--'}{tooltip.ag.horaFinal ? ` → ${tooltip.ag.horaFinal}` : ''}</span>
            {!tooltip.ag.origemTreinamento && (
              <span>👤 {(tooltip.ag.profissionais||[]).length > 0 ? (tooltip.ag.profissionais||[]).join(', ') : tooltip.ag.profissional}</span>
            )}
            {tooltip.ag.observacoes && <span style={{ fontSize:11, color:'#5f5874' }}>{tooltip.ag.observacoes.substring(0, 60)}</span>}
            <span>📌 {CLASSIFICACOES[classif]?.label}</span>
            {tooltip.ag.linkMeet && (
              <a href={tooltip.ag.linkMeet} target="_blank" rel="noopener noreferrer"
                style={{ fontSize:12, color:'#1d4ed8', fontWeight:600, pointerEvents:'all' }}>
                🎥 Entrar na reunião
              </a>
            )}
          </div>
        );
      })()}

      {/* Modal novo evento */}
      {modalAberto && (
        <div style={modalBase} onClick={fecharModal}>
          <div className="mobile-modal" style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ color: '#1c1530' }}>{editandoId ? 'Editar Evento' : 'Novo Evento'}</h3>
              <button style={s.modalFechar} onClick={fecharModal}><X size={18}/></button>
            </div>

            {/* Classificação (checkboxes) */}
            <div style={s.campo}>
              <label style={s.label}>Classificação da Atividade</label>
              <div style={s.checkboxGrupo}>
                {Object.entries(CLASSIFICACOES)
                  .filter(([k]) => !classifPermitidas || classifPermitidas.includes(k))
                  .map(([k,v]) => (
                  <label key={k} style={{ ...s.checkboxItem, ...(form.classificacao===k ? { borderColor: v.corHex, background: `${v.corHex}22` } : {}) }}>
                    <input
                      type="radio" name="classificacao" value={k}
                      checked={form.classificacao===k}
                      onChange={e => setForm({...form, classificacao: e.target.value})}
                      style={{ display: 'none' }}
                    />
                    <div style={{ ...s.checkDot, background: v.corHex }}/>
                    {v.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="campo-grid-form" style={s.campoGrid}>
              <div style={s.campo}>
                <label style={s.label}>Data *</label>
                <input type="date" style={s.input} value={form.data} onChange={e => setForm({...form, data: e.target.value})}/>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Hora de início</label>
                <input type="time" style={s.input} value={form.horaInicio} onChange={e => setForm({...form, horaInicio: e.target.value})}/>
              </div>
            </div>

            <div className="campo-grid-form" style={s.campoGrid}>
              <div style={s.campo}>
                <label style={s.label}>Hora final</label>
                <input type="time" style={s.input} value={form.horaFinal} onChange={e => setForm({...form, horaFinal: e.target.value})}/>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Duração calculada</label>
                <div style={{ ...s.input, background:'#ffffff', color: horasCalculadas > 0 ? '#10b981' : '#5f5874' }}>
                  {horasCalculadas > 0 ? `${horasCalculadas.toFixed(1)}h (${Math.round(horasCalculadas*60)} min)` : '—'}
                </div>
              </div>
            </div>

            {/* Pausa para almoço */}
            {!isPessoal && (
              <div style={s.pausaCard}>
                <label style={s.checkRowPausa}>
                  <div style={{ ...s.pausaToggle, ...(form.pausaAlmoco ? s.pausaToggleOn : {}) }}>
                    <div style={{ ...s.pausaToggleKnob, ...(form.pausaAlmoco ? s.pausaToggleKnobOn : {}) }}/>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.pausaAlmoco}
                    onChange={e => setForm({...form, pausaAlmoco: e.target.checked})}
                    style={{ display:'none' }}
                  />
                  <div>
                    <div style={s.pausaLabel}>🍽️ Pausa para almoço</div>
                    <div style={s.pausaSub}>O tempo será descontado da duração total</div>
                  </div>
                </label>
                {form.pausaAlmoco && (
                  <div style={s.pausaDuracao}>
                    <label style={s.label}>Duração da pausa</label>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {[30,45,60,90,120].map(min => (
                        <button
                          key={min}
                          style={{ ...s.btnPausa, ...(form.duracaoAlmoco===min ? s.btnPausaAtivo : {}) }}
                          onClick={() => setForm({...form, duracaoAlmoco: min})}
                        >
                          {min} min
                        </button>
                      ))}
                    </div>
                    <div style={s.pausaAviso}>
                      ⏱ {form.duracaoAlmoco} min descontados · duração líquida: {Math.max(0, calcularHoras(form.horaInicio, form.horaFinal, false, 0)*60 - form.duracaoAlmoco)} min
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={s.campo}>
              <label style={s.label}>Profissional(is) *</label>
              <div style={s.checkboxGrupo}>
                {(profissionais||[]).map(p => {
                  const selecionado = (form.profissionais||[]).includes(p);
                  return (
                    <label key={p} style={{ ...s.checkboxItem, ...(selecionado ? { borderColor:'#7c3aed', background:'rgba(124,58,237,0.15)', color:'#5b21b6' } : {}) }}>
                      <input type="checkbox" checked={selecionado} onChange={() => toggleProfissional(p)} style={{ display:'none' }} />
                      <div style={{ ...s.checkDot, background: selecionado ? '#7c3aed' : '#ebe7f3' }}/>
                      {p}
                    </label>
                  );
                })}
              </div>
            </div>

            {!isPessoal && (
              <div style={s.campo}>
                <label style={s.label}>Cliente</label>
                <select style={s.input} value={form.cliente} onChange={e => setForm({...form, cliente: e.target.value})}>
                  <option value="">Selecione</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            )}

            {form.classificacao === 'REUNIAO' && (
              <div style={s.pausaCard}>
                <label style={s.checkRowPausa}>
                  <div style={{ ...s.pausaToggle, ...(form.faturarReuniao ? s.pausaToggleOn : {}) }}>
                    <div style={{ ...s.pausaToggleKnob, ...(form.faturarReuniao ? s.pausaToggleKnobOn : {}) }}/>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.faturarReuniao}
                    onChange={e => setForm({...form, faturarReuniao: e.target.checked})}
                    style={{ display:'none' }}
                  />
                  <div>
                    <div style={s.pausaLabel}>💰 Faturar esta reunião</div>
                    <div style={s.pausaSub}>
                      Marque quando a reunião deve ser cobrada do cliente (ex.: reunião com cliente Y feita durante consultoria no cliente X). Por padrão reuniões não entram no faturamento.
                    </div>
                  </div>
                </label>
              </div>
            )}


            <div style={s.campo}>
              <label style={s.label}>Observações</label>
              <textarea style={{ ...s.input, minHeight: 70, resize: 'vertical' }} value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})}/>
            </div>

            {/* Link de Reunião */}
            <div style={s.campo}>
              <label style={s.label}>Link da Reunião</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="url"
                  placeholder="Cole aqui o link do Teams, Zoom, Meet..."
                  style={{ ...s.input, flex: 1 }}
                  value={form.linkMeet}
                  onChange={e => setForm({...form, linkMeet: e.target.value})}
                />
                <a
                  href="https://teams.microsoft.com/l/meeting/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#6264a7', color: 'white', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap' }}
                >
                  🎥 Iniciar Teams
                </a>
              </div>
              {form.linkMeet && (
                <a href={form.linkMeet} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12, color: '#1d4ed8', marginTop: 2 }}>
                  🔗 Abrir link salvo
                </a>
              )}
            </div>

            <div className="mobile-modal-footer" style={s.modalFooter}>
              {editandoId && (
                <button style={s.btnExcluir} onClick={excluirEvento}>Excluir</button>
              )}
              <button style={s.btnCancelar} onClick={fecharModal}>Cancelar</button>
              <button style={s.btnSalvar} onClick={salvarEvento}>
                {editandoId ? 'Salvar alterações' : 'Salvar evento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { background:'#ffffff', borderRadius:14, border:'1px solid #e5e0ef', padding:'1.5rem', position:'relative' },
  header: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' },
  headerLeft: { display:'flex', alignItems:'center', gap:'1rem' },
  headerRight: { display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' },
  titulo: { fontSize:'1.6rem', fontWeight:700, color:'#1c1530' },
  navBtns: { display:'flex', gap:4 },
  navBtn: { background:'transparent', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', cursor:'pointer', padding:'4px 8px', display:'flex', alignItems:'center' },
  btnNovo: { display:'flex', alignItems:'center', gap:6, background:'#7c3aed', color:'white', border:'none', borderRadius:20, padding:'8px 18px', fontSize:13, fontWeight:600, cursor:'pointer' },
  btnLimpar: { display:'flex', alignItems:'center', gap:4, background:'transparent', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', cursor:'pointer', padding:'6px 12px', fontSize:12 },
  select: { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'7px 12px', fontSize:13, cursor:'pointer' },
  menuClientes: { position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:50, background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:10, boxShadow:'0 10px 30px rgba(28,21,48,0.15)', padding:6, minWidth:260, maxHeight:340, overflowY:'auto', display:'flex', flexDirection:'column' },
  menuClientesTodos: { background:'transparent', border:'none', borderBottom:'1px solid #eeeaf5', textAlign:'left', padding:'8px 10px', fontSize:13, fontWeight:600, color:'#7c3aed', cursor:'pointer' },
  menuClientesItem: { display:'flex', alignItems:'center', gap:8, padding:'7px 10px', fontSize:13, color:'#1c1530', cursor:'pointer', borderRadius:6 },
  grade: { display:'grid', gridTemplateColumns:'repeat(7, 1fr)', border:'1px solid #eeeaf5', borderRadius:10, overflow:'hidden' },
  cabDia: { background:'#f7f5fb', color:'#7c3aed', fontWeight:700, fontSize:12, textAlign:'center', padding:'10px 0', letterSpacing:'0.05em', textTransform:'uppercase', borderBottom:'1px solid #eeeaf5' },
  celula: { background:'#ffffff', borderRight:'1px solid #eeeaf5', borderBottom:'1px solid #eeeaf5', minHeight:110, padding:'6px 8px', display:'flex', flexDirection:'column', gap:3 },
  celulaVazia: { background:'#ffffff', opacity:0.4 },
  celulaHoje: { background:'#f7f3ff', boxShadow:'inset 0 0 0 1px #7c3aed' },
  numDia: { fontSize:13, fontWeight:500, color:'#5f5874', marginBottom:2 },
  numDiaHoje: { color:'#fff', background:'#7c3aed', borderRadius:'50%', width:22, height:22, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700 },
  eventosLista: { display:'flex', flexDirection:'column', gap:2, flex:1 },
  evento: { borderRadius:4, padding:'3px 6px', fontSize:11, fontWeight:600, color:'white', cursor:'pointer', display:'flex', flexDirection:'column', gap:1 },
  eventoHora: { fontSize:11, fontWeight:700 },
  eventoProf: { fontSize:10, opacity:0.85 },
  eventoCliente: { fontSize:10, opacity:0.75, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  totalDia: { fontSize:11, color:'#10b981', fontWeight:600, marginTop:2 },
  legenda: { display:'flex', gap:'1.5rem', marginTop:'1rem', flexWrap:'wrap' },
  legendaItem: { display:'flex', alignItems:'center', gap:6 },
  legendaCor: { width:12, height:12, borderRadius:3 },
  legendaNome: { fontSize:12, color:'#5f5874' },
  tooltip: { position:'fixed', background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, padding:'10px 14px', display:'flex', flexDirection:'column', gap:4, fontSize:12, color:'#1c1530', boxShadow:'0 8px 24px rgba(0,0,0,0.5)', zIndex:9999, pointerEvents:'none', minWidth:180 },
  modal: { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:14, padding:'1.5rem', width:'100%', maxWidth:520, maxHeight:'90vh', overflowY:'auto', display:'flex', flexDirection:'column', gap:'1rem' },
  modalHeader: { display:'flex', justifyContent:'space-between', alignItems:'center' },
  modalFechar: { background:'transparent', border:'none', color:'#5f5874', cursor:'pointer', display:'flex', alignItems:'center' },
  modalFooter: { display:'flex', justifyContent:'flex-end', gap:'0.75rem', marginTop:'0.5rem' },
  campo: { display:'flex', flexDirection:'column', gap:6 },
  campoGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' },
  label: { fontSize:12, fontWeight:600, color:'#5f5874', textTransform:'uppercase', letterSpacing:'0.05em' },
  input: { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'8px 12px', fontSize:14, outline:'none', width:'100%' },
  checkboxGrupo: { display:'flex', gap:8, flexWrap:'wrap' },
  checkboxItem: { display:'flex', alignItems:'center', gap:6, padding:'6px 14px', border:'1.5px solid #e5e0ef', borderRadius:20, cursor:'pointer', fontSize:13, color:'#1c1530', userSelect:'none' },
  checkDot: { width:10, height:10, borderRadius:'50%', flexShrink:0 },
  btnCancelar: { background:'transparent', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', cursor:'pointer', padding:'8px 18px', fontSize:13 },
  btnSalvar: { background:'#7c3aed', border:'none', borderRadius:8, color:'white', cursor:'pointer', padding:'8px 18px', fontSize:13, fontWeight:600 },
  btnExcluir: { background:'transparent', border:'1px solid #ef4444', borderRadius:8, color:'#ef4444', cursor:'pointer', padding:'8px 18px', fontSize:13, marginRight:'auto' },
  pausaCard:        { background:'rgba(245,158,11,0.05)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:10, padding:'1rem', display:'flex', flexDirection:'column', gap:'0.75rem' },
  checkRowPausa:    { display:'flex', alignItems:'center', gap:'0.75rem', cursor:'pointer', userSelect:'none' },
  pausaToggle:      { width:40, height:22, borderRadius:11, background:'#ebe7f3', position:'relative', transition:'background 0.2s', flexShrink:0 },
  pausaToggleOn:    { background:'#f59e0b' },
  pausaToggleKnob:  { width:16, height:16, borderRadius:'50%', background:'white', position:'absolute', top:3, left:3, transition:'left 0.2s' },
  pausaToggleKnobOn:{ left:21 },
  pausaLabel:       { fontSize:14, fontWeight:600, color:'#b45309' },
  pausaSub:         { fontSize:11, color:'#92400e', marginTop:1 },
  pausaDuracao:     { display:'flex', flexDirection:'column', gap:'0.5rem', paddingTop:'0.5rem', borderTop:'1px solid rgba(245,158,11,0.15)' },
  btnPausa:         { background:'transparent', border:'1px solid rgba(245,158,11,0.3)', borderRadius:8, color:'#92400e', cursor:'pointer', padding:'6px 12px', fontSize:12, fontWeight:500 },
  btnPausaAtivo:    { background:'rgba(245,158,11,0.2)', border: '1px solid #f59e0b', color:'#b45309', fontWeight:700 },
  pausaAviso:       { fontSize:11, color:'#f59e0b', background:'rgba(245,158,11,0.1)', borderRadius:6, padding:'6px 10px' },
};

export { CLASSIFICACOES };
